import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Account } from "../../../../../models/account";
import { Controller, FieldValues, Path, UseFormReturn, FieldName, FieldErrors } from "react-hook-form";
import { DocumentStructure } from "../../../../../models/document-structure";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message"
import { CssField } from "../../MuiInput.styled";
import { getValueByPath } from "@shared/lib/utils";
import { Car } from "../../../../../models/car";
import { buildAutocompleteFilter } from "../buildAutocompleteFilter";
import { AUTOCOMPLETE_LISTBOX_PROPS, SEARCH_RESULTS_PER_PAGE } from "../autocompleteListboxProps";
import { AccountStructure } from "../../../../../models/accounts-structure";
import { useLazySearchCarsBrowseQuery } from "@features/tickets/api/cars.api";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors";

interface CarSearchProps<T extends FieldValues> {
    fieldStructure: AccountStructure | DocumentStructure, // field data
    useForm: UseFormReturn<T>; // form
    onSelect: (object: Car) => void, // update the form after object selected
    prefixPath?: string, // prefix path for the field in the form
    disabled?: boolean, // disable the field
}

function CarSearch<T extends FieldValues>({ fieldStructure, useForm, onSelect, prefixPath, disabled}: CarSearchProps<T>) {
    const [triggerSearchCars] = useLazySearchCarsBrowseQuery();
    const { showBoundary } = useErrorBoundary();


    const formPath = (prefixPath? prefixPath + "." : "" ) + fieldStructure.field_name;

    const [options, setOptions] = useState<Car[]>([]);
    const [inputValue, setInputValue] = useState("");
    

    useEffect(() => {

        if (inputValue.length < 3) {
            setOptions([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {

                const path = fieldStructure.field_name;
                const cars = await fetchCars(path as Path<T>, inputValue);

                if (!cancelled) {
                    setOptions(cars);
                }
            }
            catch (e) {
                if (!cancelled) {
                    handleErrorWithBoundary(e, showBoundary);
                }
            }
        })();

        return () => {
            cancelled = true;
        };

    }, [inputValue, fieldStructure.field_name]);


    /**
     * search cars by key and value
     * @param key - account key name 
     * @param value - value for search
     * @returns cars
     */
    async function fetchCars(key: Path<T>, value: string): Promise<Car[]> {
        
        const response = await triggerSearchCars({
            filter: buildAutocompleteFilter(String(key), value),
            per_page: SEARCH_RESULTS_PER_PAGE,
            sort: ["id"],
        }).unwrap();

        return response.data;
    }


    /**
     * update the form with a new value
     * @param val - the new value, can be a specific field value or the whole object itself
     */
    function onChange(val: string | Car | null): void {

        if (typeof val === "string") {
            useForm.setValue(formPath as  Path<T>, val as any);
            return;
        }

        if (val === null) {
            useForm.setValue(formPath as  Path<T>, null as any); // or reset relevant fields
            return;
        }

        onSelect(val);
    }


    function renderOption(option: Car): string {

        if(fieldStructure.field_name === "license_number"){
            return String(getValueByPath(option, fieldStructure.field_name as keyof Car));
        }

        return String(option["license_number" as keyof Car]+ ": " + String(getValueByPath(option, fieldStructure.field_name as keyof Car)));

    }


    function getValueByFieldName(car: Car): string {
        const path = fieldStructure.field_name;
        const value = getValueByPath(car, path)
        return String(value);
    }

    return (
        <CssField sx={{ minWidth: 200 }} error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}>
            <Controller
                name={formPath as Path<T>}
                control={useForm.control}
                render={({ field }) => (
                    <Autocomplete<Car, false, false, true>
                    {...field}
                    freeSolo
                    clearOnBlur={false}
                    options={options}
                    value={null}
                    getOptionLabel={(option) =>typeof option === "string" ? option : getValueByFieldName(option)}
                    inputValue={String(field.value ?? "")}
                    onChange={(_, newValue)=> onChange(newValue)}
                    disabled={disabled}
                    ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}

                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {renderOption(option)}
                        </li>
                    )}

                    onInputChange={(_, newInput) => {
                        field.onChange(newInput);
                        setInputValue(newInput);
                    }}

                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={fieldStructure.field_display_name}
                            error={useForm?.formState?.errors?.[fieldStructure.field_name] ? true : false}
                            helperText={<ErrorMessage errors={useForm.formState.errors} name={fieldStructure.field_name as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>} />}
                        />
                    )}
                    />
                )}
            />
        </CssField>
        
    );
}

export default CarSearch;